import * as React from "react";
import React__default, { memo, forwardRef, useState, useId, useCallback } from "react";
import clsx from "clsx";
import { a as FieldFooter } from "./FieldFooter.C6wlZlKr.mjs";
import { F as FieldLabel } from "./FieldLabel.D6ciKs0A.mjs";
import { S as ScreenReaderContent } from "./ScreenReaderContent.CmT4o4X9.mjs";
import { b as Paragraph } from "./Paragraph.DAcb0zW2.mjs";
import { c as checkboxStyles } from "./styles.module.CUKyFbde.mjs";
import { L as List } from "./List.DA5sxQBI.mjs";
const SvgCheck1Px = (props) => /* @__PURE__ */ React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: 20, height: 20, fill: "currentColor", viewBox: "0 0 20 20", ...props }, /* @__PURE__ */ React.createElement("path", { fillRule: "evenodd", d: "M17.854 4.646a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.708 0l-5-5a.5.5 0 0 1 .708-.708L7.5 14.293l9.646-9.647a.5.5 0 0 1 .708 0", clipRule: "evenodd" }));
const CHECKBOX_VARIANTS = ["default", "minimal"];
const Checkbox = memo(
  forwardRef(
    ({
      className,
      defaultChecked = false,
      onChange,
      icon,
      label,
      helperMessage,
      successMessage,
      errorMessage,
      showFooter = true,
      variant = CHECKBOX_VARIANTS[0],
      theme = "white",
      ...props
    }, ref) => {
      const [checked, setChecked] = useState(defaultChecked);
      const hasHelper = showFooter && !!(helperMessage || errorMessage || successMessage);
      const helperId = useId();
      return /* @__PURE__ */ React__default.createElement("div", { className: clsx(checkboxStyles.checkbox, className) }, /* @__PURE__ */ React__default.createElement(
        "label",
        {
          className: clsx(checkboxStyles.label, checkboxStyles[variant], checkboxStyles[theme], {
            [checkboxStyles.disabled]: props.disabled,
            [checkboxStyles.invalid]: !!errorMessage,
            [checkboxStyles.checked]: checked
          })
        },
        /* @__PURE__ */ React__default.createElement(
          ScreenReaderContent,
          {
            ref,
            as: "input",
            type: "checkbox",
            checked,
            onChange: (e) => {
              const state = e.target.checked;
              setChecked(state);
              if (onChange) {
                onChange(state, props.value);
              }
            },
            "aria-describedby": hasHelper ? helperId : void 0,
            "aria-invalid": showFooter && !!errorMessage ? true : void 0,
            ...props
          }
        ),
        /* @__PURE__ */ React__default.createElement("span", { className: checkboxStyles.indicator }, /* @__PURE__ */ React__default.createElement(SvgCheck1Px, null)),
        variant === CHECKBOX_VARIANTS[0] && icon && /* @__PURE__ */ React__default.createElement("span", { className: checkboxStyles.icon }, icon),
        /* @__PURE__ */ React__default.createElement(Paragraph, { size: "sm", as: "span" }, label)
      ), showFooter && /* @__PURE__ */ React__default.createElement(
        FieldFooter,
        {
          id: helperId,
          helperMessage,
          successMessage,
          errorMessage
        }
      ));
    }
  )
);
function getNewValue(value, { checked, optionValue }) {
  if (checked) {
    value.push(optionValue);
  } else {
    value.splice(value.indexOf(optionValue), 1);
  }
  return value;
}
const CheckboxGroup = forwardRef(
  ({
    className,
    id,
    defaultValue = [],
    disabled,
    onChange,
    label,
    labelVisible,
    requiredLabel,
    helperMessage,
    successMessage,
    errorMessage,
    options,
    required,
    variant,
    ...props
  }, ref) => {
    const [value, setValue] = useState(defaultValue);
    const isChecked = useCallback(
      (optionValue) => {
        return defaultValue.includes(optionValue);
      },
      [defaultValue]
    );
    const handleCheckboxChange = useCallback(
      (checked, optionValue) => {
        const newValue = getNewValue(value, { checked, optionValue });
        setValue(newValue);
        if (onChange) {
          onChange(newValue);
        }
      },
      [value, onChange]
    );
    return /* @__PURE__ */ React__default.createElement(
      "fieldset",
      {
        "data-testid": "ui-checkbox-group",
        id,
        ref,
        className: clsx(checkboxStyles.checkbox, className)
      },
      /* @__PURE__ */ React__default.createElement(
        FieldLabel,
        {
          label: /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, label, /* @__PURE__ */ React__default.createElement(
            FieldFooter,
            {
              "data-testid": "checkbox-group-aria-footer",
              as: ScreenReaderContent,
              helperMessage,
              successMessage,
              errorMessage
            }
          )),
          labelVisible,
          required,
          requiredLabel,
          as: "legend"
        }
      ),
      /* @__PURE__ */ React__default.createElement(
        List,
        {
          variant: "minimal",
          items: options.map((option) => /* @__PURE__ */ React__default.createElement(
            Checkbox,
            {
              ...props,
              ...option,
              key: option.value,
              variant,
              defaultChecked: isChecked(option.value),
              disabled: option.disabled || disabled,
              errorMessage,
              onChange: (checked, value2) => handleCheckboxChange(checked, value2),
              showFooter: false
            }
          ))
        }
      ),
      /* @__PURE__ */ React__default.createElement(
        FieldFooter,
        {
          "data-testid": "checkbox-group-footer",
          "aria-hidden": "true",
          helperMessage,
          successMessage,
          errorMessage
        }
      )
    );
  }
);
export {
  Checkbox as C,
  CheckboxGroup as a
};
//# sourceMappingURL=CheckboxGroup.Dr6BvnNA.mjs.map
